import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, FileCheck2, Send, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Modal } from '@/components/ui'
import {
  useCancelPacket,
  useGenerateFinals,
  useMarkApproved,
  useMarkRejected,
  useMarkSent,
  usePacket,
  useRegeneratePacket,
} from '../api/queries'
import { HazmatOverrideForm } from '../components/HazmatOverrideForm'
import { PacketFileList } from '../components/PacketFileList'
import { PartyBlockPreview } from '../components/PartyBlockPreview'
import { StageHistoryTimeline } from '../components/StageHistoryTimeline'
import { STAGE_LABELS, STAGE_VARIANT, transitionAllowed } from '../utils/stage-meta'

export function PacketDetailPage() {
  const params = useParams({ strict: false }) as { packetId?: string }
  const packetId = params.packetId
  if (!packetId) {
    return <div className="p-8 text-tertiary">Invalid packet id.</div>
  }
  return <PacketDetailView packetId={Number(packetId)} />
}

type PendingAction = 'approve' | 'reject' | 'cancel' | 'finals' | null

function PacketDetailView({ packetId }: { packetId: number }) {
  const detail = usePacket(packetId)
  const packet = detail.data

  const regenerate = useRegeneratePacket()
  const markSent = useMarkSent()
  const markApproved = useMarkApproved()
  const markRejected = useMarkRejected()
  const cancelPacket = useCancelPacket()
  const generateFinals = useGenerateFinals()

  const [pending, setPending] = useState<PendingAction>(null)
  const [reason, setReason] = useState('')

  if (detail.isLoading || !packet) {
    return (
      <div className="flex h-full flex-col bg-canvas">
        <DetailHeader />
        <div className="flex-1 overflow-auto p-8 text-tertiary">
          {detail.isLoading ? 'Loading…' : 'Packet not found.'}
        </div>
      </div>
    )
  }

  function onError(err: unknown) {
    const msg = err instanceof Error ? err.message : 'action failed'
    toast.error(msg, { duration: 8000 })
  }

  function closePending() {
    setPending(null)
    setReason('')
  }

  function confirmPending() {
    if (pending === 'approve') {
      markApproved.mutate(packet!.id, { onSuccess: () => { toast.success('Packet approved'); closePending() }, onError })
    } else if (pending === 'reject') {
      markRejected.mutate(
        { packetId: packet!.id, reason: reason.trim() || undefined },
        { onSuccess: () => { toast.success('Packet rejected'); closePending() }, onError },
      )
    } else if (pending === 'cancel') {
      cancelPacket.mutate(
        { packetId: packet!.id, reason: reason.trim() || undefined },
        { onSuccess: () => { toast.success('Packet cancelled'); closePending() }, onError },
      )
    } else if (pending === 'finals') {
      generateFinals.mutate(packet!.id, { onSuccess: () => { toast.success('Finals generated'); closePending() }, onError })
    }
  }

  const canMarkSent = transitionAllowed(packet.stage, 'sent_to_intermediary')
  const canApprove = transitionAllowed(packet.stage, 'approved')
  const canReject = transitionAllowed(packet.stage, 'rejected')
  const canGenerateFinals = transitionAllowed(packet.stage, 'finals_generating')
  const canCancel = transitionAllowed(packet.stage, 'cancelled')

  const anyActionPending =
    markSent.isPending || markApproved.isPending || markRejected.isPending || cancelPacket.isPending || generateFinals.isPending

  return (
    <div data-density="comfortable" className="flex h-full flex-col bg-canvas">
      <header className="flex flex-wrap items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <Link to="/export-docs">
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} />
            Export Docs
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-role-data-primary truncate m-0">
            {packet.picking_name ?? <em className="text-tertiary">unnamed shipment</em>}
          </h1>
        </div>
        <Chip size="xs" variant={STAGE_VARIANT[packet.stage]} dot>
          {STAGE_LABELS[packet.stage]}
        </Chip>
        {packet.sale_order_name ? (
          <span className="text-xs text-tertiary">SO {packet.sale_order_name}</span>
        ) : null}
        {packet.lane_name ? <span className="text-xs text-tertiary">· {packet.lane_name}</span> : null}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canMarkSent ? (
            <Button
              variant="secondary"
              size="sm"
              loading={markSent.isPending}
              disabled={anyActionPending}
              onClick={() =>
                markSent.mutate(packet.id, {
                  onSuccess: () => toast.success('Sent to intermediary'),
                  onError,
                })
              }
            >
              <Send size={14} className="mr-1.5" />
              Mark Sent
            </Button>
          ) : null}
          {canApprove ? (
            <Button variant="primary" size="sm" disabled={anyActionPending} onClick={() => setPending('approve')}>
              <CheckCircle2 size={14} className="mr-1.5" />
              Mark Approved
            </Button>
          ) : null}
          {canReject ? (
            <Button variant="danger" size="sm" disabled={anyActionPending} onClick={() => setPending('reject')}>
              <XCircle size={14} className="mr-1.5" />
              Mark Rejected
            </Button>
          ) : null}
          {canGenerateFinals ? (
            <Button variant="primary" size="sm" disabled={anyActionPending} onClick={() => setPending('finals')}>
              <FileCheck2 size={14} className="mr-1.5" />
              Generate Finals
            </Button>
          ) : null}
          {canCancel ? (
            <Button variant="ghost" size="sm" disabled={anyActionPending} onClick={() => setPending('cancel')}>
              Cancel
            </Button>
          ) : null}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          {packet.error_message ? (
            <div className="rounded-sm border border-danger bg-danger-soft px-3 py-2 text-sm text-danger">
              {packet.error_message}
            </div>
          ) : null}
          {packet.rejection_reason ? (
            <div className="rounded-sm border border-warning bg-warning-soft px-3 py-2 text-sm text-primary">
              <span className="font-medium">Rejected: </span>
              {packet.rejection_reason}
            </div>
          ) : null}

          <PartyBlockPreview packet={packet} />

          <PacketFileList
            packet={packet}
            regenerating={regenerate.isPending}
            onRegenerate={() =>
              regenerate.mutate(packet.id, {
                onSuccess: () => toast.success('Regenerating drafts'),
                onError,
              })
            }
          />

          {packet.is_hazardous ? <HazmatOverrideForm key={packet.id} packet={packet} /> : null}

          <section className="rounded-md border border-border-default bg-surface p-4">
            <h3 className="text-role-section-label m-0 mb-2">History</h3>
            <StageHistoryTimeline entries={packet.stage_history} />
          </section>
        </div>
      </div>

      <Modal
        open={pending === 'approve'}
        onOpenChange={(next) => !next && closePending()}
        tone="info"
        title="Mark this packet approved?"
        description="Confirms the intermediary has signed off on the draft documents. This unlocks Generate Finals — do this only after real approval came back from Sertrading, not while still waiting."
        footer={
          <>
            <Button variant="ghost" onClick={closePending} disabled={markApproved.isPending}>
              Cancel
            </Button>
            <Button variant="primary" loading={markApproved.isPending} onClick={confirmPending}>
              Mark Approved
            </Button>
          </>
        }
      />

      <Modal
        open={pending === 'reject'}
        onOpenChange={(next) => !next && closePending()}
        tone="danger"
        title="Mark this packet rejected?"
        description="Sends this packet back to draft regeneration. Record why the intermediary rejected it so the next draft actually fixes the problem."
        footer={
          <>
            <Button variant="ghost" onClick={closePending} disabled={markRejected.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={markRejected.isPending} onClick={confirmPending}>
              Mark Rejected
            </Button>
          </>
        }
      >
        <ReasonInput value={reason} onChange={setReason} placeholder="Why did the intermediary reject this? (optional but recommended)" />
      </Modal>

      <Modal
        open={pending === 'cancel'}
        onOpenChange={(next) => !next && closePending()}
        tone="danger"
        title="Cancel this export packet?"
        description="Cancels the whole document set for this shipment. This is a terminal state — there is no transition back except reopening via a fresh packet."
        footer={
          <>
            <Button variant="ghost" onClick={closePending} disabled={cancelPacket.isPending}>
              Keep packet
            </Button>
            <Button variant="danger" loading={cancelPacket.isPending} onClick={confirmPending}>
              Cancel packet
            </Button>
          </>
        }
      >
        <ReasonInput value={reason} onChange={setReason} placeholder="Why is this being cancelled? (optional but recommended)" />
      </Modal>

      <Modal
        open={pending === 'finals'}
        onOpenChange={(next) => !next && closePending()}
        tone="warning"
        title="Generate final documents?"
        description={
          <>
            Renders the final, signed document set for <strong>{packet.picking_name ?? 'this shipment'}</strong> from
            the approved drafts. Only do this after real intermediary approval — finals are what actually ships with
            the cargo.
          </>
        }
        footer={
          <>
            <Button variant="ghost" onClick={closePending} disabled={generateFinals.isPending}>
              Cancel
            </Button>
            <Button variant="primary" loading={generateFinals.isPending} onClick={confirmPending}>
              Generate Finals
            </Button>
          </>
        }
      />
    </div>
  )
}

function ReasonInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="w-full rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
    />
  )
}

function DetailHeader() {
  return (
    <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
      <Link to="/export-docs">
        <Button size="sm" variant="ghost">
          <ArrowLeft size={14} />
          Export Docs
        </Button>
      </Link>
      <span className="text-role-section-label">Export packet</span>
    </header>
  )
}
